import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { BannerCtaTarget, SurfaceBanner } from '@/lib/banner/useBanners';
import { bannerCtaPath } from './cta-route';

export interface BannerSlotProps {
  /** Server-ordered banners to display. When empty the whole slot renders nothing. */
  banners: SurfaceBanner[];
  className?: string;
}

/** One slide's image + optional CTA. Only the CTA is interactive. */
function BannerSlide({ banner, onCta }: { banner: SurfaceBanner; onCta: (target: BannerCtaTarget) => void }) {
  return (
    <div className="relative min-w-0 shrink-0 basis-full pl-3">
      <div className="relative overflow-hidden rounded-card">
        {banner.imageUrl && (
          <img src={banner.imageUrl} alt={banner.cta?.label ?? ''} className="w-full rounded-card object-cover" />
        )}
        {banner.cta && (
          <Button type="button" size="md" className="absolute bottom-4 left-4" onClick={() => onCta(banner.cta!.target)}>
            {banner.cta.label}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Full-width banner carousel for the top of the lobby / lander (ARC-112).
 *
 * Presentational: it takes already-fetched `banners` (the container picks the
 * guest vs lobby hook by auth state — see {@link LobbyBannerSlot}). Reuses the
 * Embla + autoplay setup from `RecommendedCarousel`: one banner per view,
 * looping, auto-advancing every 10s, with a pagination-dot rail. Each slide
 * shows the banner image full-width; when a `cta` is present it renders a CTA
 * button that routes to the target — **only the CTA is tappable**, a
 * CTA-less banner is fully static. Renders nothing when there are no banners
 * (no header, no placeholder).
 */
export function BannerSlot({ banners, className }: BannerSlotProps) {
  const navigate = useNavigate();
  const [activeDot, setActiveDot] = useState(0);

  const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'start', loop: true, duration: 15 }, [
    Autoplay({ delay: 10_000, stopOnInteraction: false, stopOnMouseEnter: true }),
  ]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setActiveDot(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi]);

  if (banners.length === 0) return null;

  const handleCta = (target: BannerCtaTarget) => navigate(bannerCtaPath(target));

  return (
    // Width cap tracks the feed below it (760px → 1300px at xl, cf. CategoryBanner / BinaryLobbyFeed).
    <div className={cn('flex w-full max-w-[760px] xl:max-w-[1300px] flex-col gap-2 px-3.5 md:px-0', className)}>
      {/* Embla viewport — the `-ml-3` container + `pl-3` slide is Embla's 12px margin-gap pattern. */}
      <div ref={emblaRef} className="w-full overflow-hidden">
        <div className="-ml-3 flex items-stretch">
          {banners.map((banner, i) => (
            <BannerSlide key={`${banner.slot}-${i}`} banner={banner} onCta={handleCta} />
          ))}
        </div>
      </div>

      {/* Pagination dots — only when there's more than one banner to page through. */}
      {banners.length > 1 && (
        <div className="flex items-center justify-center gap-1">
          {banners.map((banner, i) => (
            <button
              key={`${banner.slot}-${i}`}
              type="button"
              aria-label={`Go to banner ${i + 1}`}
              aria-current={i === activeDot}
              onClick={() => emblaApi?.scrollTo(i)}
              className={cn(
                'h-[5px] rounded-pill bg-purple border-none p-0 transition-all duration-200',
                i === activeDot ? 'w-[21px]' : 'w-[7px] opacity-50',
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
